import React, { memo, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useObservable } from 'react-use-observable';

import { Tooltip, Button, Rate, Table } from 'antd';
import { Icon } from '@eduzz/ui-antd-theme';

import Alert from 'components/Alert';
import Spin from 'components/Spin';
import { formatDate } from 'helpers/date';
import { tap } from 'rxjs/operators';
import Truncate from 'v2/components/Shared/TruncateText';
import type { IProducerRating } from 'v2/interfaces/rating';
import producerRatingService from 'v2/services/rating/producer';

const List = memo(() => {
  useObservable(() => producerRatingService.loadRatings(), []);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);
  const [isLoading] = useObservable(() => producerRatingService.isLoading(), []);

  const [data] = useObservable(() => producerRatingService.getRatings(), []);

  const [pagination] = useObservable(() => producerRatingService.getPaginationResponse(), []);

  const handleTablePaginationChange = (page: number, pageSize: number) => {
    if (pageSize !== pagination?.per_page) {
      producerRatingService.setPagination({ ...pagination, per_page: pageSize, current_page: 1 });
      return;
    }

    producerRatingService.setPagination({ ...pagination, current_page: page });
  };

  const handleClickModerate = useCallback(async (rating: IProducerRating) => {
    setIsConfirmationOpen(true);
    const confirm = await Alert.show({
      message: `Deseja realmente tornar essa avaliação ${rating.moderated ? 'invisível' : 'visível'} na vitrine?`,
      title: `Tornar ${rating.moderated ? 'invisível' : 'visível'} na vitrine`,
      confirmation: true,
      buttonOk: 'Sim',
      buttonCancel: 'Não'
    });

    if (!confirm) {
      setIsConfirmationOpen(false);
      return;
    }

    producerRatingService
      .updateRating({ ...rating, ...{ moderated: !rating.moderated } })
      .pipe(
        tap(() => {
          toast.success('Avaliação atualizada com sucesso');
          producerRatingService.updateListRatings();
        })
      )
      .subscribe();
    setIsConfirmationOpen(false);
    return;
  }, []);

  const columns = useMemo(
    () => [
      {
        title: 'Cliente',
        dataIndex: 'rater',
        key: 'rater'
      },
      {
        title: 'Data',
        dataIndex: 'rated_at',
        key: 'rated_at',
        width: 140,
        render: (ratedAt: string) => formatDate(ratedAt)
      },
      {
        title: 'Avaliação',
        dataIndex: 'rating',
        key: 'rating',
        width: 200,
        render: (rating: number | string) => (
          <>
            <Rate value={Number(rating)} disabled allowHalf className='text-sm' />
            <p className='m-0! p-1'>{rating} / 5</p>
          </>
        )
      },
      {
        title: 'Comentário',
        dataIndex: 'comment',
        key: 'comment',
        ellipsis: true,
        render: (comment: string) => (
          <Truncate
            className='w-full max-w-[75vw] overflow-hidden text-ellipsis whitespace-nowrap'
            count={120}
            final='...'
            title={comment}
          >
            {comment}
          </Truncate>
        )
      },
      {
        title: '',
        key: 'action',
        width: 72,
        fixed: 'right' as const,
        align: 'right' as const,
        render: (_: unknown, item: IProducerRating) => (
          <Tooltip placement='left' title={`Tornar ${item.moderated ? 'invisível' : 'visível'} na vitrine`}>
            <Button
              type='text'
              shape='circle'
              icon={item.moderated ? <Icon.EyeOff  /> : <Icon.Eye  />}
              onClick={() => handleClickModerate(item)}
              disabled={isConfirmationOpen}
            />
          </Tooltip>
        )
      }
    ],
    [handleClickModerate, isConfirmationOpen]
  );

  return (
    <div className='bg-surface-default mt-4 w-full rounded-sm shadow-sm'>
      <Table<IProducerRating>
        rowKey={item => String(item.payload_id)}
        columns={columns}
        dataSource={data || []}
        scroll={{ x: 800 }}
        loading={{ spinning: Boolean(isLoading), indicator: <Spin /> }}
        locale={{ emptyText: 'Nenhuma avaliação encontrada.' }}
        pagination={{
          current: pagination?.current_page || 1,
          pageSize: pagination?.per_page || 10,
          total: pagination?.total_items || 0,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 30, 40],
          onChange: handleTablePaginationChange,
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`
        }}
      />
    </div>
  );
});

export default List;
